import Hero from '@src/components/Hero';
import PageHero from '@src/components/Hero/PageHero';
import PageLayout from '@src/components/PageLayout';
import PageTheme from '@src/components/PageTheme';
import {SalesEventsQuery} from '@graphql-types';
import GridOneCol from '@src/components/Grid/GridOneCol';
import PageSection from '@src/components/PageSection';
import {getLanguageByCode} from '@src/i18n/utils';
import {graphql, PageProps} from 'gatsby';
import {flatten} from 'lodash';
import * as React from 'react';
import {Helmet} from 'react-helmet-async';
import {Trans, useTranslation} from 'react-i18next';
import SalesEventsAgendaView from './SalesEventsAgendaView';
import SalesEventsForm from './SalesEventsForm';
import {parseDate, showNextYear} from '@src/utils';
import BreadCrumbs from '@src/components/BreadCrumbs';
import {isAfter, isBefore, isToday, addDays} from 'date-fns';
import {previewNextYear} from '@src/utils/persistence';
import {useNow} from '@src/components/NowProvider';

import {WindowLocation} from '@reach/router';

type SalesEvents = SalesEventsQuery['allSalesEvents']['nodes'][number];
type SalesMarkets = SalesEventsQuery['allSalesMarkets']['nodes'][number];

const getSalesEventsForMarket = (
  salesEvents: SalesEvents[],
  salesMarket: SalesMarkets | undefined,
  today: Date,
) => {
  if (!salesMarket) {
    return [];
  }

  const zipCodes = flatten(
    salesMarket.counties.map(county =>
      county.county.zip_codes.map(zipCode => zipCode.zip_code),
    ),
  );
  return salesEvents.filter(salesEvent => {
    const eventDate = parseDate(salesEvent.zoneless_start_time_at);
    const isTodayOrAfterToday = isToday(eventDate) || isAfter(eventDate, today);
    const isBeforeOneMonthFromNow = previewNextYear()
      ? true
      : isBefore(eventDate, addDays(today, 30));
    // sales events are filtered on state, county and zip code to match
    // the sales market zips, counties and states.
    // This is necessary check as some zip codes can belong to multple
    // counties and belong to different sales markets.
    return (
      salesEvent.sales_event_venue.state === salesMarket.state &&
      salesMarket.counties.some(
        county =>
          county.county.name.toLowerCase() ===
          salesEvent.sales_event_venue.county_name.toLowerCase(),
      ) &&
      zipCodes.includes(salesEvent.sales_event_venue.zip) &&
      isTodayOrAfterToday &&
      isBeforeOneMonthFromNow
    );
  });
};

interface SalesEventsPageContext {
  id?: string;
  slug?: string;
}

const SalesEventsPage = ({
  data: {
    allLanguages: {nodes: languages},
    allPrismicMainNavigation: navigation,
    allPrismicNotifications: notifications,
    allSalesEvents: {nodes: salesEvents},
    allSalesMarkets: {nodes: salesMarkets},
    site: {
      siteMetadata: {lastBuildTime, siteName, siteUrl, supportedZipcodes},
    },
  },
  location,
  pageContext,
}: PageProps<SalesEventsQuery, SalesEventsPageContext>) => {
  const translations = [
    {
      href: `/sales-events/${pageContext.slug ?? ''}`,
      lang: getLanguageByCode('en-us', languages),
    },
    {
      href: `/es/eventos-de-ventas/${pageContext.slug ?? ''}`,
      lang: getLanguageByCode('es-mx', languages),
    },
  ];

  return (
    <PageLayout
      {...PageLayout.dateProps(lastBuildTime)}
      languages={languages}
      location={location}
      navigation={navigation}
      notifications={notifications}
      siteName={siteName}
      siteUrl={siteUrl}
      translations={translations}>
      <SalesEventsPageComponent
        id={pageContext.id}
        location={location}
        salesEvents={salesEvents}
        salesMarkets={salesMarkets}
        supportedZipcodes={supportedZipcodes}
      />
    </PageLayout>
  );
};

// use an inner component so we can get "now" from the provider inserted in PageLayout
const SalesEventsPageComponent: React.FC<{
  id: string | undefined;
  location: WindowLocation;
  salesEvents: SalesEvents[];
  salesMarkets: SalesMarkets[];
  supportedZipcodes: SalesEventsQuery['site']['siteMetadata']['supportedZipcodes'];
}> = props => {
  const now = useNow();
  const {t} = useTranslation('SalesEvents');

  const salesMarket = props.id
    ? props.salesMarkets.find(market => market.id === props.id)
    : undefined;

  return (
    <>
      <Helmet>
        <title>{t('title')}</title>
      </Helmet>
      <PageTheme theme="white">
        {salesMarket && (
          <BreadCrumbs
            slug={props.location && props.location.pathname}
            title={salesMarket.name}
          />
        )}
        <Hero>
          <PageSection noBottomPadding={false}>
            <PageHero>
              <h1>{t('pageHeading')}</h1>
            </PageHero>
          </PageSection>
          {showNextYear(now) ? (
            <>
              <PageSection noBottomPadding={false}>
                <GridOneCol align="center">
                  <p>
                    <Trans i18nKey="pageSub" t={t}>
                      Learn about your options and see if a Medicare Advantage
                      plan is right for you. Call us at{' '}
                      <strong>1-800-351-6139 (TTY 711)</strong> to save your
                      spot.
                    </Trans>
                  </p>
                </GridOneCol>
              </PageSection>
              <PageSection noBottomPadding={true}>
                {salesMarket ? (
                  <SalesEventsAgendaView
                    salesEvents={getSalesEventsForMarket(
                      props.salesEvents,
                      salesMarket,
                      now,
                    )}
                  />
                ) : (
                  <SalesEventsForm
                    salesMarkets={props.salesMarkets}
                    supportedZipCodes={props.supportedZipcodes}
                  />
                )}
              </PageSection>
            </>
          ) : (
            <PageSection noBottomPadding={false}>
              <GridOneCol align="center">
                <p>
                  <Trans i18nKey="pageSubInactive" t={t}>
                    We don't have any events right now. If you want to learn
                    more about your Medicare options, call us at{' '}
                    <strong>1-800-351-6139 (TTY 711)</strong>.
                  </Trans>
                </p>
              </GridOneCol>
            </PageSection>
          )}
        </Hero>
      </PageTheme>
    </>
  );
};

export default SalesEventsPage;

export const query = graphql`
  query SalesEvents {
    ...PageLayout

    allSalesEvents {
      nodes {
        id
        name
        start_time
        duration_minutes
        zoneless_start_time_at
        time_zone
        language
        record_locator
        sales_event_venue {
          id
          name
          address1
          address2
          city
          county_name
          state
          zip
        }
      }
    }
    allSalesMarkets {
      nodes {
        counties {
          county {
            name
            code
            zip_codes {
              zip_code
            }
          }
        }
        id
        name
        state
      }
    }
    site {
      siteMetadata {
        supportedZipcodes {
          zip
          state
          counties
          county_codes
        }
      }
    }
  }
`;
